import { useState } from "react";
import { FormDataConsumer, SelectInput, useNotify, useRefresh } from "react-admin";
import { Typography, Grid, InputLabel, Button, Box } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ReportIcon from "@mui/icons-material/Report";
import EditIcon from "@mui/icons-material/Edit";
import FormModal from "server/components/Modals/FormModal";
import { administrationRequests } from "api/administrationApi";
import { candidateRequests } from "api/candidateApi";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";

const ChangeScoreComponentsModal = ({ record }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();

  const [showData, setShowData] = useState(null);
  const [specialityNames, setSpecialityNames] = useState({});
  const [sending, setSending] = useState({}); // rows successfully sent → swap to success message
  const [pending, setPending] = useState({}); // rows with an in-flight PUT → guards against double-submit

  const handleScoreLineData = async () => {
    try {
      const [scoreLine, specialities] = await Promise.all([
        dataProvider.customRequest(candidateRequests.getCandidacyManualPickScoreComponentForUpdate(record.id)),
        dataProvider.customRequest(administrationRequests.getSpecialitiesSelect()),
      ]);

      setShowData(Object.entries(scoreLine.data).map(x => ({ id: x[0], choices: x[1] })));

      const specialitiesMap = {};
      Object.keys(scoreLine.data).forEach(id => {
        const spec = specialities.data.find(x => x.id === Number(id));
        specialitiesMap[id] = spec ? spec.name : "Неизвестна специалност";
      });

      setSpecialityNames(specialitiesMap);
    } catch (err) {
      notifyError(err);
    }
  };

  const handleSend = async (specialityId, newComponentId) => {
    if (pending[specialityId]) return; // guard срещу двоен submit
    setPending(prev => ({ ...prev, [specialityId]: true }));
    try {
      await dataProvider.customRequest(
        candidateRequests.updateCandidacyManualPickScoreComponent(record.id),
        { newComponentId },
        "PUT",
      );

      setSending(prev => ({ ...prev, [specialityId]: true })); // mark row as sent only after success
      notify("Успешно сменени компоненти за балообразуване", { type: "success" });
      refresh();
    } catch (e) {
      notifyError(e);
    } finally {
      setPending(prev => ({ ...prev, [specialityId]: false }));
    }
  };

  return (
    <FormModal
      triggerButton={
        <Button
          disabled={record?.hasApprovedRanking}
          startIcon={<EditIcon />}
          variant="contained"
          sx={{ width: "max-content" }}
        >
          Смени "избрани компоненти за балообразуване"
        </Button>
      }
      btnStyle={{ width: "max-content", marginTop: "0rem" }}
      onOpen={handleScoreLineData}
      titleContent="Смени избрани компоненти за балообразуване"
      hideSaveButton
      closeTitle="Затвори"
      resetProps={() => {
        setShowData(null);
        setSending({});
        setPending({});
      }}
    >
      {showData &&
        showData.map(item => (
          <FormDataConsumer key={item.id}>
            {({ formData }) => {
              const selectedValue = formData.components?.[item.id];

              return !sending[item.id] ? (
                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={5}
                    sx={{ position: "relative" }}
                  >
                    <InputLabel
                      sx={{
                        position: "relative",
                        paddingTop: "8px",
                        left: "0",
                        fontSize: "0.8rem",
                        color: "",
                      }}
                    >
                      Специалност
                    </InputLabel>
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#000",
                        marginLeft: "0.2rem",
                      }}
                      variant="h6"
                    >
                      {specialityNames[Number(item.id)]}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{ position: "relative", marginLeft: "-12px", marginRight: "12px" }}
                  >
                    <SelectInput
                      source={`components.${item.id}`}
                      label="Балообразуващ компонент"
                      choices={item.choices}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={3}
                    sx={{ marginTop: "12px" }}
                  >
                    <Button
                      type="button"
                      onClick={() => handleSend(item.id, selectedValue)}
                      color="primary"
                      variant="contained"
                      size="medium"
                      startIcon={<SaveIcon />}
                      disabled={!selectedValue || pending[item.id]}
                    >
                      Запази
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ display: "flex", gap: 1, width: "fit-content", margin: "2rem auto" }}>
                  <ReportIcon
                    color="error"
                    fontSize="large"
                    textAlign="center"
                  />
                  <Typography
                    fontSize={14}
                    sx={{ margin: "auto", display: "inline" }}
                    variant="body1"
                    color={"success"}
                  >
                    {` Успешно изпратена заявка за смяна на компонент за балообразуване за специалност: `}
                    <Typography
                      variant="body1"
                      color={"error"}
                      fontSize={14}
                      display={"inline"}
                    >
                      {specialityNames[item.id]}
                    </Typography>
                  </Typography>
                </Box>
              );
            }}
          </FormDataConsumer>
        ))}
    </FormModal>
  );
};

export default ChangeScoreComponentsModal;
